import { z } from 'zod';
import { formatCaseFacilityLocation, formatForDatePicker, formatForTimePicker } from '@features/forms/CaseForms/utils';
import { caseDonorFields, reallocationFields } from '@features/forms/CaseForms/schema';
import {
  validateDateInPast,
  validateDateTimeSelection,
  validateDonorHospitalAddress,
  validateDonorHospitalId,
  validateDonorOrRequired,
  validateOpoContact,
  validateOpoId,
} from '@features/forms/CaseForms/validators';
import { getClinicalRecordByType, isAllowedStatus } from '@/utils/helpers';
import { Case, CaseStatus, ClinicalItemType, ServiceType, WOLSType } from '@/api/apis/case';
import { Contact } from '@/api/apis/facilities';
import { NA_REGION_ID } from '@/constants/serviceRegion.constants';

export const MAX_WOLS_COMMENT_LENGTH = 1200;

const baseSchema = z.object({
  ...caseDonorFields,
  ...reallocationFields,
  serviceType: z.enum(ServiceType),
  pediatricCase: z.boolean(),
  wols: z.enum(WOLSType, { error: 'Select WOLS Location' }).optional(),
  wolsComment: z
    .string()
    .max(MAX_WOLS_COMMENT_LENGTH, { error: `Maximum ${MAX_WOLS_COMMENT_LENGTH} characters` })
    .optional()
    .or(z.literal('')),
  serviceRegionId: z.string(),
  status: z.enum(CaseStatus),
});

export type CaseDonorFormType = z.infer<typeof baseSchema>;

const hasContactPhoneNumber = (value?: string | null) => {
  const digits = value?.replace(/\D/g, '') ?? '';

  return digits.length > 3;
};

const validateOpoContactPhone = (formData: CaseDonorFormType, context: z.RefinementCtx) => {
  const canEditContactPhone = isAllowedStatus({
    statusTo: CaseStatus.AtTXCenter,
    currentStatus: formData.status,
    prohibitDeclined: true,
  });

  if (!canEditContactPhone) {
    return;
  }

  if (formData.opoContactId && !hasContactPhoneNumber(formData.opoSourceContactPhone)) {
    context.addIssue({
      code: 'custom',
      path: ['opoSourceContactPhone'],
      message: 'OPO contact phone is required',
    });
  }

  if (formData.secondaryOpoContactId && !hasContactPhoneNumber(formData.secondaryOpoContactPhone)) {
    context.addIssue({
      code: 'custom',
      path: ['secondaryOpoContactPhone'],
      message: 'Secondary OPO contact phone is required',
    });
  }
};

export const caseDonorExtendedSchema = baseSchema.superRefine((formData, context) => {
  validateOpoId(formData, context);
  validateOpoContact(formData, context);
  validateOpoContactPhone(formData, context);

  validateDonorHospitalId(formData, context);
  validateDonorHospitalAddress(formData, context);

  validateDonorOrRequired(formData, context);
  validateDateInPast(formData, context, 'donorHospitalDate', 'donorHospitalTime', 'donorHospitalTimeZone');
  validateDateTimeSelection(formData, context, 'donorHospitalDate', 'donorHospitalTime');
});

export const caseDonorEditSchema = baseSchema.superRefine((formData, context) => {
  validateOpoId(formData, context);
  validateOpoContact(formData, context);
  validateOpoContactPhone(formData, context);

  validateDonorHospitalId(formData, context);
  validateDonorHospitalAddress(formData, context);

  validateDateInPast(formData, context, 'donorHospitalDate', 'donorHospitalTime', 'donorHospitalTimeZone');
  validateDateTimeSelection(formData, context, 'donorHospitalDate', 'donorHospitalTime');
});

export const caseDonorDefaultValues = (caseData: Case): CaseDonorFormType => {
  const {
    opoDetails,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    pediatricCase,
    assignedOpoContacts,
    clinicalData,
    donorHospitalDetails,
    reAllocationCaseId,
    opoRequest,
    serviceRegionId,
    serviceType,
    status,
  } = caseData;

  const opoContact = assignedOpoContacts.find((c) => c.isPrimary) as Contact | undefined;
  const secondaryOpoContact = assignedOpoContacts.find((c) => !c.isPrimary) as Contact | undefined;

  const wolsData = getClinicalRecordByType(clinicalData ?? [], ClinicalItemType.WithdrawalOfLifeSupport);
  const wols = wolsData?.item?.answer;
  const wolsComment = wolsData?.item?.text || '';

  return {
    pediatricCase,
    donorHospitalId: donorHospitalDetails?.id || '',
    donorHospitalName: donorHospitalDetails?.name || '',
    hospitalCustomLocation: formatCaseFacilityLocation(donorHospitalDetails?.location),

    donorHospitalDate: formatForDatePicker(donorHospitalDate),
    donorHospitalTime: formatForTimePicker(donorHospitalTime),
    donorHospitalTimeZone: donorHospitalTimeZone || donorHospitalDetails?.location?.timezone || 'America/New_York',

    opoId: opoDetails?.id || '',
    opoName: opoDetails?.name || '',
    opoContactId: opoContact?.id ?? null,
    opoSourceContactPhone: opoContact?.primaryPhone || '',
    secondaryOpoContactId: secondaryOpoContact?.id || '',
    secondaryOpoContactPhone: secondaryOpoContact?.primaryPhone || '',

    wols,
    wolsComment,

    reAllocationCase: !!reAllocationCaseId,
    reAllocationCaseId,
    originalReAllocationCaseId: reAllocationCaseId,

    serviceType,

    opoRequest,
    originalOpoRequest: opoRequest,
    serviceRegionId: serviceRegionId ?? NA_REGION_ID,

    status,
  };
};
